import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress, Alert, Paper, Divider, Link } from '@mui/material';
import { getParticipantById, fetchAgeGroups, AgeGroup } from '../api/contests';

interface ParticipantProfileProps {
  participantId: string;
}

type ParticipantData = {
  age?: number;
  age_group_id?: number;
  age_group_name?: string;
  weight?: number;
  info?: string;
  links?: { link: string; description?: string }[];
};

const ParticipantProfilePage: React.FC<ParticipantProfileProps> = ({ participantId }) => {
  const [participantData, setParticipantData] = useState<ParticipantData | null>(null);
  const [loading, setLoading] = useState(true);
  const [ageGroups, setAgeGroups] = useState<AgeGroup[]>([]);

  // Fetch age groups on component mount
  useEffect(() => {
    fetchAgeGroups()
      .then(response => setAgeGroups(response))
      .catch(error => console.error('Error fetching age groups:', error));
  }, []);

  // Fetch participant data
  useEffect(() => {
    const loadParticipantData = async () => {
      try {
        const data = await getParticipantById(participantId);
        if (data) {
          const ageGroupName = ageGroups.find(group => group.age_group_id === data.age_group_id)?.age_group_name;
          setParticipantData({ ...data, age_group_name: ageGroupName });
        }
      } catch (error) {
        console.error('Error loading participant profile:', error);
      } finally {
        setLoading(false);
      }
    };

    // Only load participant data after age groups are fetched
    if (ageGroups.length > 0) {
      loadParticipantData();
    }
  }, [participantId, ageGroups]);

  if (loading) return <CircularProgress />;

  if (!participantData) {
    return <Alert severity="warning">Профиль участника не найден.</Alert>;
  }

  return (
    <Paper elevation={3} sx={{ p: 4, borderRadius: '10px', marginTop:2 }}>
      <Typography variant="h5" gutterBottom color="primary">
        Профиль участника
      </Typography>
      <Divider sx={{ mb: 3 }} />

      <Box mb={3}>
        <Typography variant="body1"><strong>Возраст:</strong> {participantData.age ?? 'Неизвестно'}</Typography>
        <Typography variant="body1"><strong>Возрастная группа:</strong> {participantData.age_group_name ?? 'Неизвестно'}</Typography>
        <Typography variant="body1"><strong>Вес:</strong> {participantData.weight ?? 'Неизвестно'}</Typography>
      </Box>

      <Box mb={3}>
        <Typography variant="body1"><strong>О себе:</strong></Typography>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
          {participantData.info ?? 'Нет данных'}
        </Typography>
      </Box>

      <Typography variant="h6" gutterBottom color="primary">Ссылки участника</Typography>
      {participantData.links?.length ? (
        participantData.links.map((link, index) => (
          <Box key={index} mb={2}>
            <Link href={link.link} target="_blank" rel="noopener noreferrer" sx={{ color: '#1976d2', textDecoration: 'none' }}>
              {link.link}
            </Link>
            <Typography variant="body2" color="textSecondary">{link.description}</Typography>
          </Box>
        ))
      ) : (
        <Typography variant="body2">Нет доступных ссылок.</Typography>
      )}
    </Paper>
  );
};

export default ParticipantProfilePage;
